import { useEffect, useRef, useCallback } from 'react'
import { X, Download } from 'lucide-react'

interface VideoLightboxProps {
  isOpen: boolean
  videoUrl: string | null
  filename: string | null
  onClose: () => void
}

export default function VideoLightbox({
  isOpen,
  videoUrl,
  filename,
  onClose,
}: VideoLightboxProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose()
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'

      // Auto-play when opened
      if (videoRef.current) {
        videoRef.current.play().catch(() => {
          // Autoplay may be blocked, user can click to play
        })
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        document.body.style.overflow = ''
      }
    }
  }, [isOpen, handleKeyDown])

  // Pause video when closing
  useEffect(() => {
    if (!isOpen && videoRef.current) {
      videoRef.current.pause()
    }
  }, [isOpen])

  if (!isOpen || !videoUrl) {
    return null
  }

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={handleOverlayClick}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors z-10"
        aria-label="Fechar"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Download button */}
      <a
        href={videoUrl}
        download={filename || 'video.mp4'}
        className="absolute top-4 right-16 p-2 text-white/80 hover:text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors z-10"
        aria-label="Baixar"
        onClick={(e) => e.stopPropagation()}
      >
        <Download className="w-6 h-6" />
      </a>

      {/* Filename */}
      {filename && (
        <div className="absolute top-4 left-4 px-3 py-1 text-white/80 bg-black/50 rounded-lg text-sm max-w-[50%] truncate">
          {filename}
        </div>
      )}

      {/* Video player */}
      <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          autoPlay
          className="max-w-full max-h-[90vh] rounded-lg"
          controlsList="nodownload"
        >
          Seu navegador não suporta a reprodução de vídeo.
        </video>
      </div>
    </div>
  )
}
